import Difficulty from 'app/frontend/types/custom/Difficulty'
import {
  deleteRequest,
  getRequest,
  postFormData,
  postRequest,
  putRequest,
} from '.'
import ItemSlot from 'app/frontend/types/custom/ItemSlot'
import dayjs from 'dayjs'
import { DateRange } from 'react-day-picker'
import {
  TeamRefreshStatus,
  TeamRefreshType,
} from 'app/frontend/types/custom/TeamRefreshStatus'
import { AuditData } from 'app/frontend/types/custom/AuditData'
import CustomDomainHostStatus from 'app/frontend/types/custom/CustomDomainHostStatuses'
import SeasonTrinket from 'app/frontend/types/custom/SeasonTrinket'
import CharacterClass from 'app/frontend/types/custom/CharacterClass'

export type EventStatisticsQuery = {
  from?: string
  to?: string
  seasonId?: number
  encounterIds?: number[]
  difficulty?: string
  optionalFilter?: 'any' | 'exclude_optional' | 'only_optional'
}

export const getTeamDetails = async (id: number): Promise<wowaudit.Team> => {
  return await getRequest(`/api/teams/${id}`)
}

export const getTeamDetailsByPath = async (
  path: string,
): Promise<wowaudit.Team> => {
  return await getRequest(`/api/teams/search?path=${encodeURIComponent(path)}`)
}

export const createTeam = async (
  guildId: number,
  team: {
    name: string
    onboardingStep?: string
  },
): Promise<{
  team: wowaudit.TeamReference
  guild: wowaudit.GuildReference
}> => {
  return await postRequest(`/api/guilds/${guildId}/teams`, { team })
}

export const duplicateTeam = async (
  guildId: number,
  params: { sourceTeamId: number; name: string },
): Promise<{ job_id: string }> => {
  return await postRequest(`/api/guilds/${guildId}/teams/duplicate`, params)
}

export const updateTeam = async (
  id: number,
  team: Partial<wowaudit.Team>,
): Promise<wowaudit.Team> => {
  return await putRequest(`/api/teams/${id}`, { team })
}

export const updatePlanAbilityDefaults = async (
  teamId: number,
  planAbilityDefaults: wowaudit.Team['planAbilityDefaults'],
): Promise<{ planAbilityDefaults: wowaudit.Team['planAbilityDefaults'] }> => {
  return await putRequest(`/api/teams/${teamId}/plan_ability_defaults`, {
    planAbilityDefaults: JSON.stringify(planAbilityDefaults),
  })
}

export const updateAutoSetupSettings = async (
  teamId: number,
  autoSetupSettings: wowaudit.Team['autoSetupSettings'],
): Promise<{ autoSetupSettings: wowaudit.Team['autoSetupSettings'] }> => {
  return await putRequest(`/api/teams/${teamId}/auto_setup_settings`, {
    autoSetupSettings: JSON.stringify(autoSetupSettings),
  })
}

export const updateEncounterOrder = async (
  teamId: number,
  payload: {
    seasonId: number
    difficulty: Difficulty
    encounterIds: number[]
  },
): Promise<{ encounterOrders: wowaudit.Team['encounterOrders'] }> => {
  return await putRequest(`/api/teams/${teamId}/encounter_order`, payload)
}

export const getDiscordReferences = async (
  id: number,
): Promise<wowaudit.DiscordReference[]> => {
  return await getRequest(`/api/teams/${id}/discord_references`)
}

export const getAccessRestrictions = async (
  id: number,
): Promise<wowaudit.AccessRestriction[]> => {
  return await getRequest(`/api/teams/${id}/access_restrictions`)
}

export const getTeamTrackingRules = async (
  id: number,
): Promise<wowaudit.TrackingRule[]> => {
  return await getRequest(`/api/teams/${id}/tracking_rules`)
}

export const getTeamDroptimizerConfigurations = async (
  id: number,
): Promise<wowaudit.DroptimizerConfiguration[]> => {
  return await getRequest(`/api/teams/${id}/droptimizer_configurations`)
}

export const getTeamAccessSummary = async (
  id: number,
): Promise<wowaudit.TeamAccessSummary> => {
  return await getRequest(`/api/teams/${id}/access_summary`)
}

export const getTeamRefreshStatus = async (
  id: number,
): Promise<{ status: TeamRefreshStatus }> => {
  return await getRequest(`/api/teams/${id}/refresh_status`)
}

export const refreshTeamData = async (
  id: number,
  refreshType: TeamRefreshType,
): Promise<{ status: TeamRefreshStatus }> => {
  return await postRequest(`/api/teams/${id}/refresh`, { refreshType })
}

export const getTeamEvents = async (
  id: number,
): Promise<wowaudit.EventReference[]> => {
  return await getRequest(`/api/teams/${id}/events`)
}

export const getTeamEvent = async (
  teamId: number,
  eventId: number,
): Promise<wowaudit.Event> => {
  return await getRequest(`/api/teams/${teamId}/events/${eventId}`)
}

export const updateTeamEvent = async (
  teamId: number,
  eventId: number,
  event: Partial<wowaudit.Event>,
): Promise<wowaudit.Event> => {
  return await putRequest(`/api/teams/${teamId}/events/${eventId}`, {
    event,
  })
}

export const updateEventParticipantStatus = async (
  teamId: number,
  eventParticipantId: number,
  status: wowaudit.EventParticipantReference['status'],
): Promise<wowaudit.EventReference> => {
  return await putRequest(
    `/api/teams/${teamId}/event_participants/${eventParticipantId}`,
    {
      event_participant: { status },
    },
  )
}

export const removeTeamMember = async (
  id: number,
  memberId: number,
): Promise<wowaudit.Team> => {
  return await deleteRequest(`/api/teams/${id}/members/${memberId}`)
}

export const leaveTeam = async (id: number): Promise<wowaudit.Team> => {
  return await deleteRequest(`/api/teams/${id}/members/leave`)
}

export const addTeamMember = async (
  teamId: number,
  characterId: number,
  specId?: number,
  role?: string,
  teamRankId?: number,
): Promise<wowaudit.Team> => {
  return await postRequest(`/api/teams/${teamId}/members`, {
    characterId,
    specId,
    role,
    teamRankId,
  })
}

export type ManualMemberParams = {
  name: string
  surname: string
  characterClass: CharacterClass
  specId?: number
  role?: string
  teamRankId?: number
}

export const addManualMember = async (
  teamId: number,
  params: ManualMemberParams,
): Promise<wowaudit.Team> => {
  return await postRequest(`/api/teams/${teamId}/manual_members`, params)
}

export const updateManualMemberClass = async (
  teamId: number,
  characterId: number,
  characterClass: CharacterClass,
): Promise<wowaudit.Team> => {
  return await putRequest(
    `/api/teams/${teamId}/manual_members/${characterId}`,
    {
      characterClass,
    },
  )
}

export const getTeamUsers = async (
  id: number,
): Promise<wowaudit.UserReference[]> => {
  return await getRequest(`/api/teams/${id}/users`)
}

export const getCharacterWishlist = async (
  id: number,
  characterId: number,
  seasonId: number,
): Promise<wowaudit.CharacterWishlist> => {
  return await getRequest(
    `/api/teams/${id}/character_wishlists/${characterId}?season_id=${seasonId}`,
  )
}

export const getTeamWishlist = async (
  id: number,
  difficulty: Difficulty,
  seasonId: number,
  configurationId?: number,
  source?: 'raid' | 'dungeon',
): Promise<wowaudit.TeamWishlist> => {
  const sourceParam = source && source !== 'raid' ? `&source=${source}` : ''
  return await getRequest(
    `/api/teams/${id}/wishlists?season_id=${seasonId}&difficulty=${difficulty}${
      configurationId ? `&configuration_id=${configurationId}` : ''
    }${sourceParam}`,
  )
}

export const getSeasonTrinkets = async (
  teamId: number,
  seasonId: number,
): Promise<SeasonTrinket[]> => {
  return await getRequest(`/api/teams/${teamId}/trinkets?season_id=${seasonId}`)
}

export const getAuditData = async (teamId: number): Promise<AuditData> => {
  return await getRequest(`/api/teams/${teamId}/audit_data`)
}

export const getTeamEventStatistics = async (
  teamId: number,
  query: EventStatisticsQuery,
): Promise<wowaudit.EventStatistics> => {
  const searchParams = new URLSearchParams()
  if (query.from) searchParams.set('from', query.from)
  if (query.to) searchParams.set('to', query.to)
  if (query.seasonId) searchParams.set('seasonId', query.seasonId.toString())
  if (query.encounterIds?.length) {
    query.encounterIds.forEach((encounterId) => {
      searchParams.append('encounterIds[]', encounterId.toString())
    })
  }
  if (query.difficulty) searchParams.set('difficulty', query.difficulty)
  if (query.optionalFilter) {
    searchParams.set('optionalFilter', query.optionalFilter)
  }

  return await getRequest(
    `/api/teams/${teamId}/event_statistics?${searchParams.toString()}`,
  )
}

export const getOverviewData = async (
  teamId: number,
): Promise<wowaudit.OverviewData> => {
  return await getRequest(`/api/teams/${teamId}/overview_data`)
}

export type LootHistoryUrlOptions = {
  teamId: number
  difficulty?: Difficulty
  slot?: ItemSlot
  seasonId?: number
  customRange?: DateRange
  type?: 'xlsx' | 'json'
}

export const lootHistoryUrl = ({
  teamId,
  difficulty,
  slot,
  seasonId,
  customRange,
  type = 'json',
}: LootHistoryUrlOptions) => {
  return `/api/teams/${teamId}/loot_histories.${type}?season_id=${seasonId}${
    customRange
      ? `&from=${dayjs(customRange.from).format('YYYY-MM-DD')}&to=${dayjs(
          customRange.to,
        ).format('YYYY-MM-DD')}`
      : ''
  }${difficulty ? `&difficulty=${difficulty}` : ''}${
    slot ? `&slot=${slot}` : ''
  }`
}

export const getTeamLootHistory = async (
  options: LootHistoryUrlOptions,
): Promise<wowaudit.LootHistory> => {
  return await getRequest(lootHistoryUrl(options))
}

export const updateTeamLootHistory = async (
  teamId: number,
  ids: string[],
  discarded: boolean,
): Promise<{ success: boolean }> => {
  return await postRequest(`/api/teams/${teamId}/loot_histories`, {
    ids,
    ...(discarded ? { discarded: true } : {}),
  })
}

export const getNewsItems = async (
  teamId: number,
): Promise<wowaudit.NewsItem[]> => {
  return await getRequest(`/api/teams/${teamId}/news_items`)
}

export const getNewsItem = async (
  teamId: number,
  id: number,
): Promise<wowaudit.NewsItem> => {
  return await getRequest(`/api/teams/${teamId}/news_items/${id}`)
}

export const validateCustomDomainHost = async (
  teamId: number,
  params: {
    host?: string
    subdomain?: string
  },
): Promise<{ status: CustomDomainHostStatus }> => {
  return await postRequest(
    `/api/teams/${teamId}/custom_domain_hosts/validate`,
    params,
  )
}

export const deleteCustomDomainHost = async (
  teamId: number,
  id: number,
): Promise<wowaudit.Team> => {
  return await deleteRequest(`/api/teams/${teamId}/custom_domain_hosts/${id}`)
}

export const refreshCustomDomainHost = async (
  teamId: number,
  id: number,
): Promise<wowaudit.Team> => {
  return await postRequest(
    `/api/teams/${teamId}/custom_domain_hosts/${id}/refresh`,
  )
}

export const uploadFile = async (
  teamId: number,
  file: File,
): Promise<{ url: string }> => {
  const formData = new FormData()
  formData.append('file', file)
  return await postFormData(`/api/teams/${teamId}/files`, formData)
}
